import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './declaration.css';
import PopupModel from '../../popup-model/PopupModel'; // Path as provided
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import {
  checkSubmissionStatus,
  completeEditRequest
} from '../../../services/candidate/api';
import { sendNotificationToClient } from '../../../services/candidate/notificationapi';
import { useCandidateStatus } from '../CandidateStatusContext';

const EDIT_TYPES = [
  'PERSONAL_DETAILS',
  'ADDRESS_DETAILS',
  'EDUCATION_DETAILS',
  'EMPLOYMENT_DETAILS',
];

const Declaration = () => {
  const [isAgreed, setIsAgreed] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const candidate_id = localStorage.getItem("user_name");

  const { loading, error, onboardingCompleted, latestRequest, refresh } =
    useCandidateStatus();

  // Don't allow submit until the backend status is known. Otherwise a
  // candidate who is already onboarded could trigger the first-time flow.
  const statusKnown = !loading && !error && onboardingCompleted !== null;

  const handleCheckboxChange = () => {
    setIsAgreed(!isAgreed);
  };

  const handleSubmit = () => {
    if (isAgreed && statusKnown) {
      setShowConfirmPopup(true);
    }
  };

  const closeConfirmPopup = () => {
    setShowConfirmPopup(false);
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (!onboardingCompleted) {
        // FIRST-TIME ONBOARDING: only here do we call check-submission
        const response = await checkSubmissionStatus();

        if (!response.data?.success) {
          setShowConfirmPopup(false);
          setSubmissionResult({
            isSuccess: false,
            title: 'Submission Error',
            message:
              response.message ||
              'Submission failed. Please complete all required sections.'
          });
          setShowResultPopup(true);
          return;
        }

        try {
          await sendNotificationToClient({
            title: "Candidate Submission Details",
            message: `Candidate ${candidate_id} has successfully submitted his/her details.`,
            senderId: candidate_id,
            userRole: "CANDIDATE",
            receiverId: "",
            isRead: false,
            redirectUrl: ""
          });
        } catch (notificationError) {
          console.error("Client notification failed:", notificationError);
        }
      } else {
        // EDIT FLOW: edits are finished, so lock the approved sections now.
        // (The section pages no longer complete their request on save.)
        const approvedTypes = EDIT_TYPES.filter(
          (t) => latestRequest(t)?.status === 'APPROVED'
        );

        for (const type of approvedTypes) {
          await completeEditRequest(type);
        }
      }

      setShowConfirmPopup(false);
      await refresh({ silent: true });

      setSubmissionResult({
        isSuccess: true,
        title: "Success",
        message: "Application submitted successfully."
      });
      setShowResultPopup(true);

      setTimeout(() => {
        navigate('/candidate/candidate-dashboard');
      }, 1500);

    } catch (err) {
      setShowConfirmPopup(false);
      await refresh({ silent: true });

      setSubmissionResult({
        isSuccess: false,
        title: 'Submission Error',
        message: err.response?.data?.message || 'Submission failed. Please try again.'
      });
      setShowResultPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeResultPopup = () => {
    setShowResultPopup(false);
    setSubmissionResult(null);
  };

  return (
    <>
      <h2 className='decl-header'>Declaration</h2>

      <div className="decl-container">
        <div className="decl-content-area">
          <p className="decl-text">
            This following page is regarding the previous details filled and attachments.
          </p>

          <ul className="decl-list">
            <li>Personal Details</li>
            <li>Address Details</li>
            <li>Education Details</li>
            <li>Employment Details (Not required for freshers)</li>
          </ul>

          <div className="decl-agreement">
            <label className="decl-checkbox-label">
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
              />
              <span className="custom-checkbox">
                {isAgreed && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
              </span>
              I agree that all the details and attachments were filled by me.
            </label>
          </div>
        </div>

        <div className="decl-bottom-nav">
          <button
            className="decl-submit"
            disabled={!isAgreed || !statusKnown}
            onClick={handleSubmit}
          >
            Submit <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>

        {showConfirmPopup && (
          <div className="popup-overlay">
            <div className="popup-content">
              <h3 className='popup-title'>Confirmation</h3>
              <p>Click OK to Submit the application.</p>
              <div className="dec-action-btn">
                <button
                  className="decl-popup-close"
                  onClick={closeConfirmPopup}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <button
                  className="decl-popup-submit"
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "OK"}
                </button>
              </div>
            </div>
          </div>
        )}

        <PopupModel
          show={showResultPopup}
          onClose={closeResultPopup}
          title={submissionResult?.title}
          message={submissionResult?.message}
          isSuccess={submissionResult?.isSuccess}
        />
      </div>
    </>
  );
};

export default Declaration;